<template>
    <div v-if="isLoading" class="col-12 text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
        </div>
    </div>

    <div v-else class="todo-board-container">
        <div class="scroll-hint scroll-hint-left" v-show="showLeftScrollHint && !disableScroll" @click="scrollLeft">
            <i class="fas fa-angle-left"></i>
        </div>

        <div class="to-do-board-hover">
            <div class="to-do-board-wrapper position-relative px-0" @mouseenter="onHover = true" @mouseleave="onHover = false">
                 <div ref="scrollContainer" class="scroll-container" :class="{ 'no-scroll': disableScroll }">
                    <div class="row flex-nowrap to-do-board-columns mx-0" :class="{ 'no-scroll': disableScroll }">
                        <div v-for="(column, index) in visibleColumns" :key="`${column.key}-${renderKeyMap[column.key]}`" :class="['board-column', 'col-md-3', getColumnPaddingClass(index, visibleColumns.length)]">
                            <div class="card column-card mb-0">
                                <div class="card-header text-white fw-bold text-caiptalize" :class="column.color">
                                    <h3 class="col-title mb-0">
                                        <font-awesome-icon :icon="['fas', column.icon]" />
                                        {{ column.title }} ({{ column.items.length }})
                                    </h3>
                                </div>
                                <div v-if="!isAllColumnsEmpty" class="card-body p-0">
                                    <table :id="`table-${column.key}`" class="d-none">
                                        <thead>
                                            <tr><th></th></tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in column.items" :key="item.id">
                                                <td>row-{{ index }}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <div v-if="paginatedItems[column.key]?.length === 0" class="col-empty text-center py-4">
                                        <img :src="imgEmptyListItem" class="empty-img" alt="To Do list empty" width="30">
                                        <h3 class="empty-text">Congratulations!</h3>
                                    </div>

                                    <div v-else class="d-flex col-items-wrapper p-2">
                                        <div class="col-items w-100">
                                            <draggable v-model="paginatedItems[column.key]" item-key="id" :group="{ name: column.key, pull: false, put: false }" :key="column.key" :animation="200" :disabled="!canDrag" :move="checkMoveAllowed" @end="() => onColumnReorder(column.key)">
                                                <to-do-column-item
                                                    v-for="(item, index) in paginatedItems[column.key]"
                                                    :key="item.id"
                                                    :role="role"
                                                    :consultant-id="consultantId"
                                                    :user-id="userId"
                                                    :date="date"
                                                    :item="item"
                                                    :index="getGlobalIndex(column.key, index)"
                                                    :column-type="column.key"
                                                    @follow-up-logs="showFollowUpLogsModal"
                                                    @follow-up="(item) => showFollowUpModal(item, column.key)"
                                                    @convert-to-prospect="showConvertToProspectModal"
                                                    @send-to-lost="showSendToLostModal"
                                                    @done="markCardAsDone"
                                                    @save="approvalRequest"
                                                    @seen="markCardAsSeen"
                                                    @decline="showDeclineModal"
                                                    @cancel-send-to-lost="cancelLostApprovalRequest"
                                                    @refresh-columns="fetchDataForAllColumns(date)"
                                                    @contacted="(item) => showContactedModal(item, column.key)"
                                                    @transfer-consultant="showTransferConsultantModal"
                                                    @drag-state-change="handleDragStateChange"
                                                />
                                            </draggable>
                                        </div>
                                        
                                        <!-- Bottom Pagination Controls (duplicate of top) -->
                                        <div v-if="column.items.length > 0" class="dt-container dt-bootstrap4 dt-empty-footer border-top border-bottom-0 mx-n2 mt-2 mb-n2">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <div class="dt-length">
                                                    <label>
                                                        <select :name="`table-${column.key}_length_bottom`" class="custom-select custom-select-sm form-control form-control-sm" @change="changePageLength(column.key, $event)">
                                                            <option value="5" :selected="getCurrentPageLength(column.key) === 5">5</option>
                                                            <option value="10" :selected="getCurrentPageLength(column.key) === 10">10</option>
                                                            <option value="20" :selected="getCurrentPageLength(column.key) === 20">20</option>
                                                            <option value="25" :selected="getCurrentPageLength(column.key) === 25">25</option>
                                                        </select>
                                                    </label>
                                                </div>
                                                <div class="dt-paging paging_simple">
                                                    <ul class="pagination">
                                                        <li class="dt-paging-button page-item" :class="{ disabled: !canGoPrevious(column.key) }" @click.prevent="!canGoPrevious(column.key) || goToPreviousPage(column.key)">
                                                            <a href="#" class="page-link previous">
                                                                <i class="fas fa-angle-left"></i>
                                                            </a>
                                                        </li>
                                                        <li class="paginate_button page-item next" :class="{ disabled: !canGoNext(column.key) }" @click.prevent="!canGoNext(column.key) || goToNextPage(column.key)">
                                                            <a href="#" class="page-link next">
                                                                <i class="fas fa-angle-right"></i>
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div> 
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="isAllColumnsEmpty" class="col-12 col-empty-all text-center py-5">
                        <img :src="imgEmptyList" class="empty-img mb-3" alt="All To Do columns empty" width="50" />
                        <h3 class="empty-text">Well done! All your task are completed.</h3>
                    </div>
                </div>

                <follow-up-logs-modal ref="followUpLogsModal" />
                <follow-up-modal ref="followUpModal" @save="saveFollowUp" />
                <convert-to-prospect-modal ref="convertToProspectModal" @save="convertToProspect" />
                <send-to-lost-modal ref="sendToLostModal" @save="sendToLost" />
                <decline-request-modal ref="declineRequestModal" @save="approvalRequest" />
                <contacted-modal ref="contactedModal" @save="saveContacted" />
                <transfer-consultant-modal ref="transferConsultantModal" @save="saveTransferConsultant" />
            </div>
        </div>
        <div class="scroll-hint scroll-hint-right" v-show="showRightScrollHint && !disableScroll" @click="scrollRight">
            <i class="fas fa-angle-right"></i>
        </div>
    </div>
</template>

<script>
import ToDoColumnItem from './ToDoColumnItem.vue';
import FollowUpLogsModal from './FollowUpLogsModal.vue';
import FollowUpModal from './FollowUpModal.vue';
import ConvertToProspectModal from './ConvertToProspectModal.vue';
import SendToLostModal from './SendToLostModal.vue';
import DeclineRequestModal from './DeclineRequestModal.vue';
import ContactedModal from './ContactedModal.vue';
import Draggable from 'vuedraggable';
import TransferConsultantModal from './TransferConsultantModal.vue';
import { todoItems, getJourneysItems, saveFollowUpSchedule, convertLeadToProspect, sendBoardToLost, markAsDone, markAsSeen, approvalFollowUpRequest, approvalSendToLostRequest, cancelSendToLost, saveColumnOrder, saveContactedStatus, saveAssignedConsultant } from '../../../services/api/to-do';

import "datatables.net-bs4";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";

export default {
    name: 'ToDoBoardColumns',
    components: { ToDoColumnItem, FollowUpLogsModal, FollowUpModal, ConvertToProspectModal, SendToLostModal, DeclineRequestModal, ContactedModal, Draggable, TransferConsultantModal },
    props: {
        date: {
            type: String,
            required: true,
            default: () => new Date().toISOString().slice(0, 10)
        },
        role: {
            type: Number,
            default: 0
        },
        consultantId: {
            type: Number,
            default: 0
        },
        userId: {
            type: Number,
            default: 0
        },
        listType: {
            type: String,
            default: 'consultant'
        },
        availableHeight: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            isLoading: false,
            hasInitialFetch: false,
            drawCount: 0,
            imgEmptyList: '/images/empty-columns.svg',
            imgEmptyListItem: '/images/empty-list.svg',
            dataTableInstances: {},
            paginatedItems: {
                leads: [],
                prospects: [],
                quotations: [],
                journeys: [],
                losts: []
            },
            renderKeyMap: {
                leads: 0,
                prospects: 0,
                quotations: 0,
                journeys: 0,
                losts: 0
            },
            columns: [
                { key: 'leads', title: 'Leads', color: 'col-leads', icon: 'user', items: [] },
                { key: 'prospects', title: 'Prospects', color: 'col-prospects', icon: 'user-check', items: [] },
                { key: 'quotations', title: 'Quote', color: 'col-quotations', icon: 'file-lines', items: [] },
                { key: 'journeys', title: 'Journey', color: 'col-students', icon: 'user-graduate', items: [] },
                { key: 'losts', title: 'Lost', color: 'col-losts', icon: 'siren-on', items: [] }
            ],
            showLeftScrollHint: false,
            showRightScrollHint: false,
            onHover: false,
            itemDragStates: {} // Track individual item drag states
        }
    },
    mounted() {
        if (this.listType !== 'consultant') {
            this.columns = this.columns.filter(col => col.key !== 'losts');
        }

        this.$nextTick(() => {
            if (this.date && !this.hasInitialFetch) {
                this.hasInitialFetch = true;
                this.fetchDataForAllColumns(this.date);
            }

            this.updateScrollHints();
            this.updateColumnHeights();
            this.$refs.scrollContainer?.addEventListener('scroll', this.updateScrollHints);
            window.addEventListener('resize', this.handleResize);
        });
    },
    computed: {
        isAllColumnsEmpty() {
            const isEmpty = this.columns.every(column => column.items.length === 0);
            
            this.$nextTick(() => {
                this.$emit('columns-empty-state', isEmpty);
            });
            return isEmpty;
        },
        canDrag() {
            const hasPermission = this.role === 0 || this.role === null || typeof this.role === 'undefined';
            
            // If no permission, return false
            if (!hasPermission) return false;
            
            // Check if any item is currently over an interactive element or text
            return !Object.values(this.itemDragStates).some(state => state === false);
        },
        isTeamLeader() {
            return [5, 15].includes(this.role);
        },
        visibleColumns() {
            // For consultants, hide Lost column if it's empty
            if (!this.isTeamLeader && this.listType === 'consultant') {
                const lostColumn = this.columns.find(col => col.key === 'losts');
                if (lostColumn && lostColumn.items.length === 0) {
                    return this.columns.filter(col => col.key !== 'losts');
                }
            }

            return this.columns;
        },
        disableScroll() {
            if (this.isTeamLeader) {
                return true;
            }
            
            // For consultants, disable scroll only if Lost column is hidden (empty)
            if (!this.isTeamLeader && this.listType === 'consultant') {
                const lostColumn = this.columns.find(col => col.key === 'losts');
                
                if (lostColumn && lostColumn.items.length === 0) {
                    return true;
                }
                
                return false;
            }
            
            return false;
        }
    },
    watch: {
        date: {
            immediate: false,
            handler(newDate, oldDate) {
                if (newDate && newDate !== oldDate && !this.isLoading) {
                    this.fetchDataForAllColumns(newDate);
                }
            }
        },
        consultantId: {
            immediate: false,
            handler(newUserId, oldUserId) {
                if (newUserId !== oldUserId && !this.isLoading && this.date) {
                    this.fetchDataForAllColumns(this.date);
                }
            }
        },
        listType() {
            this.renderColumns();
        },
        disableScroll: {
            immediate: true,
            handler(newVal) {
                this.$nextTick(() => {
                    this.updateScrollHints();
                    this.updateColumnHeights();
                });
            }
        },
        visibleColumns: {
            handler() {
                this.$nextTick(() => {
                    this.updateColumnHeights();
                });
            }
        },
        isAllColumnsEmpty: {
            handler() {
                this.$nextTick(() => {
                    this.updateColumnHeights();
                });
                
                this.$emit('columns-empty-state', this.isAllColumnsEmpty);
            }
        },
        availableHeight: {
            handler() {
                this.updateColumnHeights();
            }
        }
    },
    beforeDestroy() {
        this.$refs.scrollContainer?.removeEventListener('scroll', this.updateScrollHints);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleDragStateChange(itemId, isDraggable) {
            // Update the drag state for the specific item
            this.$set(this.itemDragStates, itemId, isDraggable);
        },
        checkMoveAllowed(evt) {
            // Check if the item being dragged is currently draggable
            const itemId = evt.draggedContext.element.id;
            return this.itemDragStates[itemId] !== false;
        },
        initDataTable(column) {
            const tableId = `#table-${column.key}`;
            const table = $(tableId);

            try {
                const instance = table.DataTable({
                    dom: '<"d-flex justify-content-between align-items-center mb-0"l p><"table-content"t>',
                    columns: [{ title: "" }],
                    paging: true,
                    pageLength: 20,
                    info: false,
                    searching: false,
                    lengthChange: true,
                    lengthMenu: [[5, 10, 20, 25], [5, 10, 20, 25]],
                    pagingType: "simple",
                    language: {
                        lengthMenu: "_MENU_",
                        paginate: {
                            previous: `<i class="fas fa-angle-left"></i>`,
                            next: `<i class="fas fa-angle-right"></i>`
                        }
                    },
                    ordering: false,
                    destroy: true,
                    drawCallback: () => this.updatePaginatedItems(column.key, column.items)
                });

                this.dataTableInstances[column.key] = instance;

                instance.clear();

                column.items.forEach((_, i) => {
                    instance.row.add([`row-${i}`]);
                });

                instance.draw();

                const wrapperSelector = `#table-${column.key}_wrapper`;

                if (column.items.length === 0) {
                    $(wrapperSelector).hide();
                } else {
                    $(wrapperSelector).show();
                }

                $(wrapperSelector).off("click", ".page-item").on("click", ".page-item", (e) => {
                    e.preventDefault();

                    const item = $(e.currentTarget);
                    const link = item.find(".page-link");

                    if (!link.length || item.hasClass("disabled")) {
                        return;
                    }

                    const dataTable = this.dataTableInstances[column.key];

                    if (!dataTable || typeof dataTable.page !== "function") {
                        return;
                    }

                    if (link.hasClass("next")) {
                        dataTable.page("next").draw("page");
                    } else if (link.hasClass("previous")) {
                        dataTable.page("previous").draw("page");
                    }
                });
            } catch (error) {
                console.error(`Error initializing DataTable for column ${column.key}:`, error);
                // Set to null to prevent further issues
                this.dataTableInstances[column.key] = null;
            }
        },
        async fetchDataForAllColumns(date) {
            this.isLoading = true;
            
            try {
                const dateParam = date || '';
                let payload = {};
                if (this.role === 5 || this.role === 15) {
                    payload = {
                        list_type: this.listType,
                        consultant_id: this.consultantId
                    };
                }

                const response = await todoItems(dateParam, payload);
                const data = response.data;
                const apiKeyMap = {
                    leads: 'Lead',
                    prospects: 'Prospect',
                    quotations: 'Quote',
                    losts: 'Lost'
                };

                // Fetch journeys from a separate endpoint
                const journeysResponse = await getJourneysItems(dateParam, payload);
                const journeysData = journeysResponse.data?.data || [];
                const isValidStatus = item => {
                    const transferStatus = item?.status_transfer_request;
                    if (!transferStatus) {
                        return ['pending'].includes(item.status);
                    }

                    if (['No Renewal', 'Lost'].includes(transferStatus.model_status)) {
                        return ['rejected'].includes(transferStatus.status);
                    }

                    return !['pending', 'approved'].includes(transferStatus.status);
                };

                this.columns.forEach(column => {
                    if (column.key === 'journeys') {
                        if (this.role === 5 || this.role === 15) {
                            column.items = Array.isArray(journeysData) ? journeysData : [];
                        } else {
                            column.items = Array.isArray(journeysData) ? journeysData.filter(isValidStatus) : [];
                        }
                    } else {
                        const apiKey = apiKeyMap[column.key];
                        column.items = Array.isArray(data[apiKey]) ? data[apiKey] : [];
                    }
                });
            } catch (error) {
                console.error(`Error fetching todo items`, error);
            } finally {
                this.isLoading = false;

                this.$nextTick(() => {
                    this.columns.forEach(column => this.initDataTable(column));

                    this.updateScrollHints();
                    this.updateColumnHeights();
                    this.$emit('data-refreshed');
                    
                    setTimeout(() => {
                        this.updateColumnHeights();
                    }, 150);
                });
            }
        },
        renderColumns() {
            const base = [
                { key: 'leads', title: 'Leads', color: 'col-leads', icon: 'user', items: [] },
                { key: 'prospects', title: 'Prospects', color: 'col-prospects', icon: 'user-check', items: [] },
                { key: 'quotations', title: 'Quote', color: 'col-quotations', icon: 'file-lines', items: [] },
                { key: 'journeys', title: 'Journey', color: 'col-students', icon: 'user-graduate', items: [] }
            ];

            if (this.listType === 'consultant') {
                base.push({ key: 'losts', title: 'Lost', color: 'col-losts', icon: 'siren-on', items: [] });
            }

            this.columns = base;
        },
        getColumnPaddingClass(index, totalColumns) {
            if (index === 0) return 'pl-0 pr-2';
            if (index === totalColumns - 1) return 'pl-2 pr-0';
            return 'px-2';
        },
        updatePaginatedItems(columnKey, allItems) {
            const dataTable = this.dataTableInstances[columnKey];

            if (!dataTable || typeof dataTable.page !== "function") {
                return;
            }

            const currentPageData = dataTable.rows({ page: "current" }).data().toArray();
            const visibleTitles = currentPageData.map(row => row[0]);

            this.paginatedItems[columnKey] = allItems.filter((_, i) => visibleTitles.includes(`row-${i}`));
            this.drawCount++;
        },
        getGlobalIndex(columnKey, indexOnPage) {
            try {
                const tableEl = document.getElementById(`${columnKey}-table`)

                if (!tableEl || !$.fn.dataTable.isDataTable(tableEl)) {
                    return indexOnPage
                }

                const table = $(tableEl).DataTable()
                const pageInfo = table.page.info()

                return (pageInfo?.start ?? 0) + indexOnPage
            } catch (e) {
                return indexOnPage;
            }
        },
        showFollowUpLogsModal(id) {
            this.$refs.followUpLogsModal.openModal({ id });
        },
        showFollowUpModal(item, columnType) {
            this.selectedItem = { ...item, column_type: columnType };
            this.$refs.followUpModal.todoId = item.id;
            this.$refs.followUpModal.openModal();
        },
        async saveFollowUp({ payload, callback }) {
            try {
                const requestPayload = {
                    id: this.selectedItem.id,
                    type: payload.type,
                    timezone: payload.timezone,
                    duration: payload.duration,
                    client_show_datetime: payload.client_date,
                    user_show_datetime: payload.user_date,
                    reason: payload.comments,
                    action_by_team_leader: this.role === 5 || this.role === 15,
                    force: payload.force || false
                };

                const response = await saveFollowUpSchedule(requestPayload);

                if (response.status === 200 || response.status === 201) {
                    if (response.data?.status === 'unavailable') {
                        callback?.({ status: 'unavailable' });
                        return;
                    } else {
                      callback?.({ status: 'success' });
                    }

                    await this.fetchDataForAllColumns(this.date);

                    const key = this.selectedItem.column_type;
                    this.renderKeyMap[key]++;
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message
                    }).show();
                }
            } catch (error) {
                console.error(`Error saving follow up schedule`, error);
            }
        },
        showConvertToProspectModal(item) {
            this.selectedItem = item;
            this.$refs.convertToProspectModal.openModal({ item });
        },
        async convertToProspect(lead) {
            this.$refs.convertToProspectModal.setLoadingState(true);

            try {
                const response = await convertLeadToProspect(lead);

                if (response.status === 200 || response.status === 201) {
                    this.$refs.convertToProspectModal.closeModal();
                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Error saving follow up schedule`, error);
            } finally {
                this.$refs.convertToProspectModal.setLoadingState(false);
            }
        },
        showSendToLostModal(content) {
            this.selectedItem = content.item;
            this.$refs.sendToLostModal.openModal({ name: content.item.student_name, title: content.title });
        },
        async sendToLost(value) {
            try {
                const payload = {
                    id: this.selectedItem.id,
                    reason_id: value.reason_id,
                    notes: value.notes,
                    action_by_team_leader: this.role === 5 || this.role === 15
                };
                const response = await sendBoardToLost(payload);

                if (response.status === 200 || response.status === 201) {
                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Error saving follow up schedule`, error);
            }
        },
        showContactedModal(item, columnType) {
            const paymentOptions = item.unpaid_payment;

            this.selectedItem = { ...item, column_type: columnType };
            this.$refs.contactedModal.todoId = item.id;
            this.$refs.contactedModal.openModal(paymentOptions);
        },
        async saveContacted({ payload }) {
            try {
                const requestPayload = {
                    id: this.selectedItem.id,
                    contacted_via: payload.via,
                    notes: payload.notes,
                    payment_status: payload.payment_status
                };
                const response = await saveContactedStatus(this.selectedItem.id, requestPayload);

                if (response.status === 200 || response.status === 201) {
                    new Noty({
                        type: "success",
                        text: response.data.message || 'Contacted status saved successfully.'
                    }).show();

                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Error saving follow up schedule`, error);
            }
        },
        async markCardAsDone(id) {
            try {
                const response = await markAsDone({ id: id });

                if (response.status === 200 || response.status === 201) {
                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Failed to mark card as done:`, error);
            }
        },
        async markCardAsSeen(id) {
          try {
            const response = await markAsSeen(id);

            if (response.status === 200 || response.status === 201) {
              await this.fetchDataForAllColumns(this.date);
            } else {
              new Noty({
                type: "error",
                text: response.data.message || 'An error occurred.'
              }).show();
            }
          } catch (error) {
            console.error(`Failed to mark card as seen:`, error);
          }
        },
        async approvalRequest(id, action, type, notes = '') {
            try {
                const payload = {
                    id: id,
                    action: action,
                }

                if (action === 'reject') {
                    payload.rejected_notes = notes;
                }

                let response;

                if (type === 'send-to-lost') {
                    payload.notes = notes;
                    response = await approvalSendToLostRequest(payload);
                } else {
                    response = await approvalFollowUpRequest(payload);
                }


                if (response.status === 200 || response.status === 201) {
                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Failed to ${action}`, error);
            }
        },
        showDeclineModal(item) {
            this.selectedItem = item;
            this.$refs.declineRequestModal.openModal({
                id: this.selectedItem.id,
                name: this.selectedItem.student_name,
                type: this.selectedItem.is_waiting_approval_for_follow_up ? 'follow-up' : 'send-to-lost'
            });
        },
        showTransferConsultantModal(item, type) {
            this.selectedItem = item;
            this.$refs.transferConsultantModal.openModal({ item, type, user_id: this.userId });
        },
        async saveTransferConsultant({ id, type, payload, callback }) {
            try {
                const response = await saveAssignedConsultant(id, type, payload);

                if (response.status === 200 || response.status === 201) {
                    if (response.data?.status === 'unavailable') {
                        callback?.({ status: 'unavailable' });
                        return;
                    } else {
                      callback?.({ status: 'success' });
                    }

                    await this.fetchDataForAllColumns(this.date);

                    const key = this.selectedItem.column_type;
                    this.renderKeyMap[key]++;
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message
                    }).show();
                }
            } catch (error) {
                console.error(`Error saving transfer consultant`, error);
            }
        },
        async cancelLostApprovalRequest(id) {
            try {
                const response = await cancelSendToLost(id);

                if (response.status === 200 || response.status === 201) {
                    await this.fetchDataForAllColumns(this.date);
                } else {
                    new Noty({
                        type: "error",
                        text: response.data.message || 'An error occurred.'
                    }).show();
                }
            } catch (error) {
                console.error(`Failed to mark card as done:`, error);
            }
        },
        async onColumnReorder(columnKey) {
            await this.saveOrder(columnKey);
        },
        async saveOrder(columnKey) {
            const orderedIds = this.paginatedItems[columnKey].map(item => item.id);
            try {
                await saveColumnOrder({
                    column: columnKey,
                    ids: orderedIds
                });
            } catch (error) {
                console.error('Error saving order:', error);
            }
        },
        // Pagination control methods
        canGoPrevious(columnKey) {
            try {
                const dataTable = this.dataTableInstances[columnKey];
                
                if (!dataTable || typeof dataTable.page !== "function") {
                    return false;
                }

                const pageInfo = dataTable.page.info();
                return pageInfo && pageInfo.page > 0;
            } catch (error) {
                console.error(`Error checking canGoPrevious for ${columnKey}:`, error);
                return false;
            }
        },
        canGoNext(columnKey) {
            try {
                const dataTable = this.dataTableInstances[columnKey];

                if (!dataTable || typeof dataTable.page !== "function") {
                    return false;
                }
                
                const pageInfo = dataTable.page.info();
                return pageInfo && pageInfo.page < pageInfo.pages - 1;
            } catch (error) {
                console.error(`Error checking canGoNext for ${columnKey}:`, error);
                return false;
            }
        },
        goToPreviousPage(columnKey) {
            try {
                const dataTable = this.dataTableInstances[columnKey];

                if (dataTable && this.canGoPrevious(columnKey)) {
                    dataTable.page("previous").draw("page");
                }
            } catch (error) {
                console.error(`Error going to previous page for ${columnKey}:`, error);
            }
        },
        goToNextPage(columnKey) {
            try {
                const dataTable = this.dataTableInstances[columnKey];

                if (dataTable && this.canGoNext(columnKey)) {
                    dataTable.page("next").draw("page");
                }
            } catch (error) {
                console.error(`Error going to next page for ${columnKey}:`, error);
            }
        },
        changePageLength(columnKey, event) {
            try {
                const dataTable = this.dataTableInstances[columnKey];

                if (dataTable && typeof dataTable.page === "function") {
                    const newLength = parseInt(event.target.value);
                    dataTable.page.len(newLength).draw();
                }
            } catch (error) {
                console.error(`Error changing page length for ${columnKey}:`, error);
            }
        },
        scrollLeft() {
            const el = this.$refs.scrollContainer;

            if (el) {
                el.scrollBy({ left: -300, behavior: 'smooth' });

                setTimeout(() => {
                    this.updateScrollHints();
                }, 300);
            }
        },
        scrollRight() {
            const el = this.$refs.scrollContainer;

            if (el) {
                el.scrollBy({ left: 300, behavior: 'smooth' });

                setTimeout(() => {
                    this.updateScrollHints();
                }, 300);
            }
        },
        // Public method that can be called to trigger height recalculation
        recalculateHeights() {
            this.updateColumnHeights();
        },
        handleResize() {
            this.updateScrollHints();
            this.updateColumnHeights();
        },
        updateColumnHeights() {
            // Use $nextTick to ensure DOM has been updated
            this.$nextTick(() => {
                let availableHeight;
                
                if (this.availableHeight) {
                    // Use the height passed from parent
                    availableHeight = this.availableHeight;
                } else {
                    // Fallback: try to calculate it ourselves
                    const cardTodoElement = document.querySelector('.card-todo');
                    
                    if (!cardTodoElement) {
                        return;
                    }

                    // Get the actual height of the .card-todo element
                    const cardTodoHeight = cardTodoElement.offsetHeight;
                    
                    // Calculate space used by other elements within .card-todo
                    const dateNavigation = cardTodoElement.querySelector('[class*="date"]');
                    const userTabs = cardTodoElement.querySelector('[class*="tab"]');
                    const alert = cardTodoElement.querySelector('.alert');
                    
                    // Calculate heights of fixed elements
                    const dateNavHeight = dateNavigation ? dateNavigation.offsetHeight : 0;
                    const userTabsHeight = userTabs ? userTabs.offsetHeight : 0;
                    const alertHeight = alert ? alert.offsetHeight : 0;
                    
                    // Account for padding and margins
                    const cardPadding = 32; // .card-todo has p-4 (1rem * 4 = 32px)
                    const columnHeaderHeight = 60; // approximate height of column headers
                    const buffer = 20; // additional buffer for margins/spacing
                    
                    // Calculate available height for .col-items-wrapper
                    const usedSpace = dateNavHeight + userTabsHeight + alertHeight + cardPadding + columnHeaderHeight + buffer;
                    availableHeight = Math.max(200, cardTodoHeight - usedSpace);
                }
                
                // Apply the calculated height to all .col-items-wrapper elements
                const colItemsWrappers = document.querySelectorAll('.col-items-wrapper');
                colItemsWrappers.forEach(wrapper => {
                    wrapper.style.maxHeight = `${availableHeight}px`;
                });
            });
        },
        updateScrollHints() {
            if (this.disableScroll) {
                this.showLeftScrollHint = false;
                this.showRightScrollHint = false;
                return;
            }

            const el = this.$refs.scrollContainer;

            if (!el) {
                return;
            }

            const scrollLeft = Math.round(el.scrollLeft);
            const scrollWidth = el.scrollWidth;
            const clientWidth = el.clientWidth;
            const maxScrollLeft = scrollWidth - clientWidth;
            const hasHorizontalScroll = scrollWidth > clientWidth;
            const tolerance = 13; // Calculated from the container's padding and scroll hint width

            this.showLeftScrollHint = hasHorizontalScroll && scrollLeft > tolerance;
            this.showRightScrollHint = hasHorizontalScroll && scrollLeft < (maxScrollLeft - tolerance);
        },
        getCurrentPageLength(columnKey) {
            try {
                const dataTable = this.dataTableInstances[columnKey];
                if (!dataTable || typeof dataTable.page !== "function") {
                    return 20; // default
                }
                return dataTable.page.len();
            } catch (error) {
                console.error(`Error getting current page length for ${columnKey}:`, error);
                return 20; // default
            }
        },
    }
}
</script>
